/**
 * CaptureMap — MapLibre map for survey capture viewer.
 *
 * @remarks
 * - CARTO Dark Matter style (no API key required).
 * - Track line (cyan translucent) connecting all captures in order.
 * - All captures as small cyan circles.
 * - Selected capture highlighted in amber + heading arrow.
 * - Auto-pans smoothly to selected capture on index change.
 * - Fit bounds to all captures on initial load.
 */

import { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef } from 'react';
import type { SurveyCapture } from '../types/survey.types';

const STYLE_DARK = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';
const STYLE_FALLBACK = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';

const SRC_TRACK = 'sv-track';
const SRC_ALL = 'sv-captures';
const SRC_SEL = 'sv-selected';
const SRC_ARROW = 'sv-arrow';

const LYR_TRACK = 'sv-track-line';
const LYR_ALL = 'sv-captures-circles';
const LYR_SEL = 'sv-selected-circle';
const LYR_ARROW = 'sv-heading-arrow';

type FC = GeoJSON.FeatureCollection;

// ── GeoJSON builders ──────────────────────────────────────────────────────────

function trackGeoJSON(captures: SurveyCapture[]): FC {
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: captures.map(c => [c.cameraLon, c.cameraLat]),
        },
        properties: {},
      },
    ],
  };
}

function allGeoJSON(captures: SurveyCapture[]): FC {
  return {
    type: 'FeatureCollection',
    features: captures.map((c, i) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [c.cameraLon, c.cameraLat] },
      properties: { index: i },
    })),
  };
}

function selectedGeoJSON(captures: SurveyCapture[], idx: number): FC {
  const c = captures[idx];
  if (!c) return { type: 'FeatureCollection', features: [] };
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [c.cameraLon, c.cameraLat] },
        properties: {},
      },
    ],
  };
}

/** Short line in the heading direction from the selected capture (~30 m). */
function arrowGeoJSON(captures: SurveyCapture[], idx: number): FC {
  const c = captures[idx];
  if (!c || c.cameraHeading === null) return { type: 'FeatureCollection', features: [] };
  const len = 0.0003; // degrees ≈ 33 m
  const rad = (c.cameraHeading * Math.PI) / 180;
  const endLon = c.cameraLon + Math.sin(rad) * len;
  const endLat = c.cameraLat + Math.cos(rad) * len;
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [c.cameraLon, c.cameraLat],
            [endLon, endLat],
          ],
        },
        properties: {},
      },
    ],
  };
}

// ── Layer setup ───────────────────────────────────────────────────────────────

function addLayers(map: MapLibreMap, captures: SurveyCapture[], idx: number): void {
  // Trajectory
  map.addSource(SRC_TRACK, { type: 'geojson', data: trackGeoJSON(captures) });
  map.addLayer({
    id: LYR_TRACK,
    type: 'line',
    source: SRC_TRACK,
    paint: { 'line-color': '#2fa4b5', 'line-width': 2, 'line-opacity': 0.45 },
  });

  // All captures
  map.addSource(SRC_ALL, { type: 'geojson', data: allGeoJSON(captures) });
  map.addLayer({
    id: LYR_ALL,
    type: 'circle',
    source: SRC_ALL,
    paint: {
      'circle-radius': 5,
      'circle-color': '#2fa4b5',
      'circle-stroke-width': 1,
      'circle-stroke-color': 'rgba(255,255,255,0.3)',
      'circle-opacity': 0.8,
    },
  });

  // Selected capture (amber)
  map.addSource(SRC_SEL, { type: 'geojson', data: selectedGeoJSON(captures, idx) });
  map.addLayer({
    id: LYR_SEL,
    type: 'circle',
    source: SRC_SEL,
    paint: {
      'circle-radius': 10,
      'circle-color': '#f59e0b',
      'circle-stroke-width': 2.5,
      'circle-stroke-color': '#fff',
    },
  });

  // Heading arrow
  map.addSource(SRC_ARROW, { type: 'geojson', data: arrowGeoJSON(captures, idx) });
  map.addLayer({
    id: LYR_ARROW,
    type: 'line',
    source: SRC_ARROW,
    layout: { 'line-cap': 'round' },
    paint: { 'line-color': '#f59e0b', 'line-width': 3, 'line-opacity': 0.9 },
  });
}

function fitCaptures(map: MapLibreMap, captures: SurveyCapture[]): void {
  const lngs = captures.map(c => c.cameraLon);
  const lats = captures.map(c => c.cameraLat);
  map.fitBounds(
    [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ],
    { padding: 60, duration: 0, maxZoom: 18 }
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

interface CaptureMapProps {
  captures: SurveyCapture[];
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
}

export function CaptureMap({
  captures,
  selectedIndex,
  onSelectIndex,
}: CaptureMapProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const loadedRef = useRef(false);
  const selectRef = useRef(onSelectIndex);
  const fallbackUsed = useRef(false);

  useEffect(() => {
    selectRef.current = onSelectIndex;
  }, [onSelectIndex]);

  // ── Init on captures change ────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || captures.length === 0) return;

    const map = new MapLibreMap({
      container: containerRef.current,
      style: STYLE_DARK,
      zoom: 14,
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    loadedRef.current = false;
    fallbackUsed.current = false;

    // Fallback if primary style fails
    map.on('error', e => {
      const msg = (e as { error?: { message?: string } }).error?.message ?? '';
      if (
        !fallbackUsed.current &&
        (msg.toLowerCase().includes('style') || msg.toLowerCase().includes('sprite'))
      ) {
        fallbackUsed.current = true;
        map.setStyle(STYLE_FALLBACK);
      }
    });

    map.on('load', () => {
      loadedRef.current = true;
      addLayers(map, captures, selectedIndex);
      fitCaptures(map, captures);

      map.on('click', LYR_ALL, e => {
        const idx = e.features?.[0]?.properties?.index as number | undefined;
        if (idx !== undefined) selectRef.current(idx);
      });
      map.on('mouseenter', LYR_ALL, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', LYR_ALL, () => {
        map.getCanvas().style.cursor = '';
      });
    });

    return (): void => {
      loadedRef.current = false;
      map.remove();
      mapRef.current = null;
    };
  }, [captures]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Update selection + smooth pan ─────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;

    (
      map.getSource(SRC_SEL) as { setData: (d: GeoJSON.FeatureCollection) => void } | undefined
    )?.setData(selectedGeoJSON(captures, selectedIndex));
    (
      map.getSource(SRC_ARROW) as { setData: (d: GeoJSON.FeatureCollection) => void } | undefined
    )?.setData(arrowGeoJSON(captures, selectedIndex));

    const c = captures[selectedIndex];
    if (c) {
      map.easeTo({ center: [c.cameraLon, c.cameraLat], duration: 350, essential: true });
    }
  }, [selectedIndex, captures]);

  return <div ref={containerRef} className="sv-map" />;
}
